// src/pages/Chat/MessageList.jsx
import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import { FiEdit2, FiCopy, FiCheck, FiX } from 'react-icons/fi';

const MessageItem = ({ msg, index, onUpdateMessage, chatId, onSendMessage, setInputContent }) => {
    const { t } = useTranslation();
    const [isEditing, setIsEditing] = useState(false);
    const [editContent, setEditContent] = useState(msg.content);
    const textareaRef = useRef(null);

    useEffect(() => {
        if (isEditing && textareaRef.current) {
            textareaRef.current.focus();
            // Set cursor to end of text
            textareaRef.current.selectionStart = textareaRef.current.value.length;
            textareaRef.current.selectionEnd = textareaRef.current.value.length;
        }
    }, [isEditing]);

    const handleEditClick = () => {
        setEditContent(msg.content);
        setIsEditing(true);
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditContent(msg.content);
    };

    const handleSaveEdit = async () => {
        if (editContent.trim() === msg.content) {
            setIsEditing(false);
            return;
        }
        
        try {

            await onUpdateMessage(chatId, index, editContent);
            setIsEditing(false);
            if (setInputContent) {
                setInputContent(editContent); // Pass the edited content to the input field
            } else {
                 console.error("setInputContent function not provided to MessageItem.");
            }

        } catch (error) {
            console.error('Error updating message:', error);
        }
    };

    const handleCopyClick = () => {
        navigator.clipboard.writeText(msg.content).then(() => {
            // Optionally show a success notification
        }).catch(err => {
            console.error('Failed to copy message: ', err);
        });
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            handleSaveEdit();
        } else if (e.key === 'Escape') {
            handleCancelEdit();
        }
    };

    return (
        <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-xl lg:max-w-3xl px-4 py-2 rounded-lg overflow-hidden w-full ${
                msg.role === 'user' ? 'bg-gray-200' : 'border border-brand-dark bg-brand-white'
            }`}>
                {/* Message Content */}
                {isEditing ? (
                    <div className="mb-2">
                        <textarea
                            ref={textareaRef}
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            onKeyDown={handleKeyDown}
                            className="w-full p-2 border border-brand-orange rounded bg-white text-black resize-none"
                            rows="4"
                        />
                    </div>
                ) : (
                    <div className="prose prose-invert">
                        <ReactMarkdown 
			    remarkPlugins={[remarkGfm]}
			    components={{
                                // THIS IS THE KEY FIX:
                                // Custom renderer for 'pre' (code blocks)
                                // overflow-x-auto allows horizontal scrolling for code
                                pre: ({node, ...props}) => (
                                    <div className="overflow-x-auto w-full my-2 rounded bg-gray-800/5 p-2">
                                        <pre {...props} style={{ margin: 0 }} />
                                    </div>
                                ),
                                // Optional: Ensure inline code wraps if needed
                                code: ({node, inline, className, children, ...props}) => {
                                    return (
                                        <code className={`${className} break-words whitespace-pre-wrap`} {...props}>
                                            {children}
                                        </code>
                                    )
                                }
                            }}				
		            >
                            {msg.content}
                        </ReactMarkdown>
                    </div>
                )}

                {/* Citations */}
                {msg.role === 'assistant' && msg.citations && msg.citations.length > 0 && (
                    <div className="mt-2 max-w-xl lg:max-w-3xl text-xs text-blue-900">
                        <h4 className="font-semibold mb-1">{t('askDoc.sourcesLabel')}:</h4>
                        <ul className="list-disc list-inside">
                            {msg.citations.map((citation, idx) => (
                                <li key={idx} className="truncate">
                                    <a href={citation} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                        [{idx + 1}] {citation}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Action Buttons */}
                {msg.role !== 'system' && (
                    <div className="mt-2 pt-2 border-t border-gray-200">
                        {isEditing ? (
                            <div className="flex space-x-2">
                                <button
                                    onClick={handleCancelEdit}
                                    className="flex items-center space-x-1 px-3 py-1 rounded bg-slate-400 text-brand-dark hover:bg-brand-orange transition-colors text-sm"
                                >
                                    <FiX size={14} />
                                    
                                </button>
                                <button
                                    onClick={handleSaveEdit}
                                    className="flex items-center space-x-1 px-3 py-1 rounded bg-slate-400 text-brand-dark hover:bg-brand-orange transition-colors text-sm"
                                >
                                    <FiCheck size={14} />
                                    
                                </button>
                            </div>
                        ) : (
                            <div className="flex space-x-2">
                                {msg.role === 'user' && (
                                    <button
                                        onClick={handleEditClick}
                                        className="flex items-center space-x-1 px-3 py-1 rounded bg-slate-400 text-white hover:bg-orange-600 transition-colors text-sm"
                                        
                                    >
                                        <FiEdit2 size={14} />
                                        
                                    </button>
                                )}
                                <button
                                    onClick={handleCopyClick}
                                    className="flex items-center space-x-1 px-3 py-1 rounded bg-slate-400 text-white hover:bg-brand-orange transition-colors text-sm"
                                    
                                >
                                    <FiCopy size={14} />
                                    
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const MessageList = ({ messages, isLoading, error, onUpdateMessage, chatId, onSendMessage, setInputContent }) => {
    const { t } = useTranslation();
    const endOfMessagesRef = useRef(null);

    useEffect(() => {
        endOfMessagesRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    if (isLoading) return <div className="flex-1 flex items-center justify-center"><p>Loading messages...</p></div>;
    if (error) return <div className="flex-1 flex items-center justify-center"><p className="text-red-500">{error}</p></div>;

    return (
        <div className="flex-1 overflow-y-auto p-6 space-y-4 text-black">
            {messages.map((msg, index) => (
                <MessageItem 
                    key={index}
                    msg={msg}
                    index={index}
                    onUpdateMessage={onUpdateMessage}
                    chatId={chatId}
                    onSendMessage={onSendMessage}
                    setInputContent={setInputContent}
                />
            ))}
            <div ref={endOfMessagesRef} />
        </div>
    );
};

export default MessageList;
